import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, Clock, Workflow, Tag, Folder, Loader2 } from 'lucide-react';
import { cn } from '@/utils/cn';
import { useSuggestions, useRecentSearches } from '../hooks/useSearch';
import { MIN_QUERY_LENGTH } from '../types';
import type { SearchSuggestion, RecentSearch } from '../types';

interface SearchBarProps {
  initialQuery?: string;
  onSearch?: (query: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  showRecentSearches?: boolean;
  navigateOnSearch?: boolean;
  className?: string;
}

export function SearchBar({
  initialQuery = '',
  onSearch,
  placeholder = 'Search workflows...',
  autoFocus = false,
  showRecentSearches = true,
  navigateOnSearch = true,
  className,
}: SearchBarProps) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [query, setQuery] = useState(initialQuery);
  const [debouncedQuery, setDebouncedQuery] = useState(initialQuery);
  const [isFocused, setIsFocused] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  // Debounce query for suggestions
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  // Fetch suggestions
  const { data: suggestionsData, isLoading: isSuggestionsLoading } =
    useSuggestions(debouncedQuery, {
      enabled: debouncedQuery.length >= MIN_QUERY_LENGTH,
    });

  // Fetch recent searches
  const { data: recentSearches } = useRecentSearches(5, {
    enabled: showRecentSearches && query.length < MIN_QUERY_LENGTH,
  });

  const suggestions = useMemo(
    () => suggestionsData?.suggestions || [],
    [suggestionsData?.suggestions]
  );
  const showDropdown =
    isFocused &&
    (suggestions.length > 0 ||
      (showRecentSearches && recentSearches && recentSearches.length > 0 && query.length < MIN_QUERY_LENGTH));

  // Handle search submission
  const handleSearch = useCallback(
    (searchQuery: string) => {
      const trimmed = searchQuery.trim();
      if (trimmed.length < MIN_QUERY_LENGTH) return;

      setQuery(trimmed);
      onSearch?.(trimmed);

      if (navigateOnSearch) {
        navigate(`/tenant/search?q=${encodeURIComponent(trimmed)}`);
      }

      inputRef.current?.blur();
    },
    [navigate, navigateOnSearch, onSearch]
  );

  // Handle suggestion selection
  const handleSelectSuggestion = useCallback(
    (suggestion: SearchSuggestion) => {
      if (suggestion.workflow_id) {
        navigate(`/tenant/workflows/${suggestion.workflow_id}`);
      } else {
        handleSearch(suggestion.text);
      }
    },
    [handleSearch, navigate]
  );

  // Handle recent search selection
  const handleSelectRecent = useCallback(
    (recent: RecentSearch) => {
      handleSearch(recent.query);
    },
    [handleSearch]
  );

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const items =
        query.length >= MIN_QUERY_LENGTH
          ? suggestions
          : (recentSearches || []);

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < items.length - 1 ? prev + 1 : prev
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < items.length) {
          const item = items[selectedIndex];
          if ('text' in item) {
            handleSelectSuggestion(item as SearchSuggestion);
          } else {
            handleSelectRecent(item as RecentSearch);
          }
        } else {
          handleSearch(query);
        }
      } else if (e.key === 'Escape') {
        inputRef.current?.blur();
      }
    },
    [
      query,
      suggestions,
      recentSearches,
      selectedIndex,
      handleSearch,
      handleSelectSuggestion,
      handleSelectRecent,
    ]
  );

  // Reset selection when suggestions change
  useEffect(() => {
    setSelectedIndex(-1);
  }, [suggestions, recentSearches]);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto focus
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  const getSuggestionIcon = (type: string) => {
    switch (type) {
      case 'workflow':
        return <Workflow className="h-4 w-4 text-blue-500" />;
      case 'tag':
        return <Tag className="h-4 w-4 text-green-500" />;
      case 'category':
        return <Folder className="h-4 w-4 text-purple-500" />;
      default:
        return <Search className="h-4 w-4 text-gray-400" />;
    }
  };

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      {/* Search input */}
      <div
        className={cn(
          'flex items-center gap-2 rounded-lg border bg-white px-3 py-2 transition-colors dark:bg-gray-800',
          isFocused
            ? 'border-blue-500 ring-1 ring-blue-500'
            : 'border-gray-300 dark:border-gray-600'
        )}
      >
        <Search className="h-5 w-5 text-gray-400" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="flex-1 bg-transparent text-sm text-gray-900 placeholder-gray-500 focus:outline-none dark:text-white dark:placeholder-gray-400"
        />
        {isSuggestionsLoading && (
          <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
        )}
        {query && (
          <button
            type="button"
            onClick={() => {
              setQuery('');
              inputRef.current?.focus();
            }}
            className="rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Dropdown */}
      {showDropdown && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
          {/* Suggestions */}
          {query.length >= MIN_QUERY_LENGTH && suggestions.length > 0 && (
            <div className="py-1">
              {suggestions.map((suggestion, index) => (
                <button
                  key={`${suggestion.type}-${suggestion.text}`}
                  onClick={() => handleSelectSuggestion(suggestion)}
                  className={cn(
                    'flex w-full items-center gap-3 px-3 py-2 text-left text-sm',
                    selectedIndex === index
                      ? 'bg-blue-50 dark:bg-blue-900/20'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                  )}
                >
                  {getSuggestionIcon(suggestion.type)}
                  <span className="flex-1 truncate text-gray-900 dark:text-white">
                    {suggestion.text}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {suggestion.type}
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* Recent searches */}
          {showRecentSearches &&
            query.length < MIN_QUERY_LENGTH &&
            recentSearches &&
            recentSearches.length > 0 && (
              <div className="py-1">
                <div className="px-3 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
                  Recent Searches
                </div>
                {recentSearches.map((recent, index) => (
                  <button
                    key={recent.id}
                    onClick={() => handleSelectRecent(recent)}
                    className={cn(
                      'flex w-full items-center gap-3 px-3 py-2 text-left text-sm',
                      selectedIndex === index
                        ? 'bg-blue-50 dark:bg-blue-900/20'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                    )}
                  >
                    <Clock className="h-4 w-4 text-gray-400" />
                    <span className="flex-1 truncate text-gray-900 dark:text-white">
                      {recent.query}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {recent.results} results
                    </span>
                  </button>
                ))}
              </div>
            )}

          {/* Keyboard hint */}
          <div className="border-t border-gray-100 px-3 py-2 dark:border-gray-700">
            <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
              <span>
                <kbd className="rounded bg-gray-100 px-1 dark:bg-gray-700">↑↓</kbd> to navigate
              </span>
              <span>
                <kbd className="rounded bg-gray-100 px-1 dark:bg-gray-700">Enter</kbd> to select
              </span>
              <span>
                <kbd className="rounded bg-gray-100 px-1 dark:bg-gray-700">Esc</kbd> to close
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Compact search bar for header
export function CompactSearchBar({ className }: { className?: string }) {
  return (
    <SearchBar
      placeholder="Search..."
      showRecentSearches={false}
      className={cn('w-64', className)}
    />
  );
}
